// src/features/tuition/components/finances/FinanceManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Avatar, 
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  ShowChart as ChartIcon,
  FileDownload as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { 
  fetchPayments, 
  fetchInvoices, 
  fetchFinancialStats 
} from '../../services/financesService';
import PaymentForm from './PaymentForm';
import InvoiceForm from './InvoiceForm';

const FinanceManagement = ({ userId }) => {
  const [activeTab, setActiveTab] = useState('payments');
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [financialStats, setFinancialStats] = useState({
    totalEarnings: 0,
    pendingPayments: 0,
    paidInvoices: 0,
    unpaidInvoices: 0
  });
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [isInvoiceFormOpen, setIsInvoiceFormOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  
  useEffect(() => {
    loadFinancialData();
    
    // For demo purposes
    setTimeout(() => {
      const mockPayments = [
        {
          id: '1',
          studentId: '1',
          studentName: 'Emma Johnson',
          amount: 450,
          date: '2025-04-15',
          method: 'bank transfer',
          status: 'paid',
          description: 'Mathematics tuition - April 2025'
        },
        {
          id: '2',
          studentId: '2',
          studentName: 'Liam Smith',
          amount: 600,
          date: '2025-04-10',
          method: 'cash',
          status: 'paid',
          description: 'Mathematics and Life Sciences tuition - April 2025'
        },
        {
          id: '3',
          studentId: '3',
          studentName: 'Sophia Williams',
          amount: 300,
          date: '2025-03-28',
          method: 'bank transfer',
          status: 'paid',
          description: 'English tuition - March 2025'
        }
      ];
      
      const mockInvoices = [
        {
          id: '1',
          studentId: '1',
          studentName: 'Emma Johnson',
          issueDate: '2025-04-01',
          dueDate: '2025-04-15',
          items: [
            { description: 'Mathematics tuition - 4 sessions', quantity: 4, rate: 150, amount: 600 }
          ],
          subtotal: 600,
          tax: 0,
          total: 600,
          status: 'paid',
          paidAmount: 600
        },
        {
          id: '2',
          studentId: '2',
          studentName: 'Liam Smith',
          issueDate: '2025-04-01',
          dueDate: '2025-04-15',
          items: [
            { description: 'Mathematics tuition - 3 sessions', quantity: 3, rate: 150, amount: 450 },
            { description: 'Life Sciences tuition - 2 sessions', quantity: 2, rate: 175, amount: 350 }
          ],
          subtotal: 800,
          tax: 0,
          total: 800,
          status: 'partial',
          paidAmount: 600
        },
        {
          id: '3',
          studentId: '3',
          studentName: 'Sophia Williams',
          issueDate: '2025-04-01',
          dueDate: '2025-04-15',
          items: [
            { description: 'English tuition - 4 sessions', quantity: 4, rate: 125, amount: 500 }
          ],
          subtotal: 500,
          tax: 0,
          total: 500,
          status: 'unpaid',
          paidAmount: 0
        }
      ];
      
      const mockStats = {
        totalEarnings: 1350,
        pendingPayments: 700,
        paidInvoices: 1,
        unpaidInvoices: 2
      };
      
      setPayments(mockPayments);
      setInvoices(mockInvoices);
      setFinancialStats(mockStats);
      setLoading(false);
    }, 1000);
  }, [userId]);
  
  const loadFinancialData = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [paymentsData, invoicesData, statsData] = await Promise.all([
        fetchPayments(userId, dateRange),
        fetchInvoices(userId, dateRange),
        fetchFinancialStats(userId)
      ]);
      
      setPayments(paymentsData);
      setInvoices(invoicesData);
      setFinancialStats(statsData);
    } catch (error) {
      console.error("Error loading financial data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleMenuOpen = (event, itemId) => {
    setAnchorEl(event.currentTarget);
    setSelectedItemId(itemId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItemId(null);
  };
  
  const handleAddPayment = () => {
    setCurrentItem(null);
    setIsPaymentFormOpen(true);
  };
  
  const handleEditPayment = () => {
    const payment = payments.find(p => p.id === selectedItemId);
    setCurrentItem(payment);
    setIsPaymentFormOpen(true);
    handleMenuClose();
  };
  
  const handleDeletePayment = async () => {
    if (!selectedItemId) return;
    
    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this payment record?")) {
      handleMenuClose();
      return;
    }
    
    // Mock deletion for now
    setPayments(payments.filter(p => p.id !== selectedItemId));
    handleMenuClose();
  };
  
  const handleAddInvoice = () => {
    setCurrentItem(null);
    setIsInvoiceFormOpen(true);
  };
  
  const handleEditInvoice = () => {
    const invoice = invoices.find(i => i.id === selectedItemId);
    setCurrentItem(invoice);
    setIsInvoiceFormOpen(true);
    handleMenuClose();
  };
  
  const handleDeleteInvoice = async () => {
    if (!selectedItemId) return;
    
    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this invoice?")) {
      handleMenuClose();
      return;
    }
    
    // Mock deletion for now
    setInvoices(invoices.filter(i => i.id !== selectedItemId));
    handleMenuClose();
  };
  
  const handlePaymentFormClose = () => {
    setIsPaymentFormOpen(false);
    setCurrentItem(null);
  };
  
  const handleInvoiceFormClose = () => {
    setIsInvoiceFormOpen(false);
    setCurrentItem(null);
  };
  
  const handlePaymentFormSubmit = (paymentData) => {
    if (currentItem) {
      // Update existing payment
      const updatedPayments = payments.map(p => 
        p.id === currentItem.id ? { ...p, ...paymentData } : p
      );
      setPayments(updatedPayments);
    } else {
      // Add new payment
      const newPayment = {
        id: `payment_${Date.now()}`,
        ...paymentData
      };
      setPayments([newPayment, ...payments]);
    }
    
    handlePaymentFormClose();
  };
  
  const handleInvoiceFormSubmit = (invoiceData) => {
    if (currentItem) {
      // Update existing invoice
      const updatedInvoices = invoices.map(i => 
        i.id === currentItem.id ? { ...i, ...invoiceData } : i
      );
      setInvoices(updatedInvoices);
    } else {
      // Add new invoice
      const newInvoice = {
        id: `invoice_${Date.now()}`,
        ...invoiceData
      };
      setInvoices([newInvoice, ...invoices]);
    }
    
    handleInvoiceFormClose();
  };
  
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };
  
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const getStatusChip = (status) => {
    switch (status) {
      case 'paid':
        return <Chip label="Paid" size="small" sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' }} />;
      case 'partial':
        return <Chip label="Partial" size="small" sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' }} />;
      case 'unpaid':
      case 'overdue':
        return <Chip label={status === 'unpaid' ? 'Unpaid' : 'Overdue'} size="small" sx={{ bgcolor: 'rgba(244, 67, 54, 0.1)', color: '#F44336' }} />;
      default:
        return <Chip label={status} size="small" sx={{ bgcolor: 'rgba(158, 158, 158, 0.1)', color: '#757575' }} />;
    }
  };
  
  const filteredPayments = payments.filter(payment => 
    payment.studentName.toLowerCase().includes(search.toLowerCase()) ||
    payment.description.toLowerCase().includes(search.toLowerCase())
  );
  
  const filteredInvoices = invoices.filter(invoice => 
    invoice.studentName.toLowerCase().includes(search.toLowerCase())
  );
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Finance stats */}
      <Grid container spacing={2} sx={{ mb: 3 }} width='100%' alignItems="stretch">
        <Grid item sx={{ width: { xs: '100%', sm: '48%', md: '23.5%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Earnings
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', width: 40, height: 40 }}>
                  <PaymentsIcon sx={{ color: '#4CAF50' }} />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(financialStats.totalEarnings)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item sx={{ width: { xs: '100%', sm: '48%', md: '23.5%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Pending Payments
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', width: 40, height: 40 }}>
                  <ReceiptIcon sx={{ color: '#FF9800' }} />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(financialStats.pendingPayments)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Outstanding
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item sx={{ width: { xs: '100%', sm: '48%', md: '23.5%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Paid Invoices
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', width: 40, height: 40 }}>
                  <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {financialStats.paidInvoices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item sx={{ width: { xs: '100%', sm: '48%', md: '23.5%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Unpaid Invoices
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(244, 67, 54, 0.1)', width: 40, height: 40 }}>
                  <WarningIcon sx={{ color: '#F44336' }} />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {financialStats.unpaidInvoices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Needs attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Main content */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Financial Management
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={activeTab === 'payments' ? <PaymentsIcon /> : <ReceiptIcon />}
              onClick={activeTab === 'payments' ? handleAddPayment : handleAddInvoice}
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              {activeTab === 'payments' ? 'Add Payment' : 'Create Invoice'}
            </Button>
          </Box>
        </Box>
        
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab 
            icon={<PaymentsIcon />} 
            iconPosition="start" 
            label="Payments" 
            value="payments" 
          />
          <Tab 
            icon={<ReceiptIcon />} 
            iconPosition="start" 
            label="Invoices" 
            value="invoices" 
          />
          <Tab 
            icon={<ChartIcon />} 
            iconPosition="start" 
            label="Reports" 
            value="reports" 
          />
        </Tabs>
        
        <Box sx={{ mt: 3 }}>
          {/* Search and date filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                placeholder={`Search ${activeTab === 'payments' ? 'payments' : 'invoices'}...`}
                value={search}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                name="start"
                value={dateRange.start}
                onChange={handleDateRangeChange}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                name="end"
                value={dateRange.end}
                onChange={handleDateRangeChange}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Payments Tab Content */}
          {activeTab === 'payments' && (
            <>
              {filteredPayments.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <PaymentsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No payment records found
                  </Typography>
                  <Typography color="text.secondary" paragraph>
                    {search ? 'Try changing your search criteria' : 'Get started by adding your first payment'}
                  </Typography>
                  {!search && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddPayment}
                      sx={{
                        backgroundColor: THEME_COLORS.primary,
                        '&:hover': { backgroundColor: '#1e1c4e' },
                        mt: 2
                      }}
                    >
                      Add Payment
                    </Button>
                  )}
                </Box>
              ) : (
                <TableContainer>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPayments.map((payment) => (
                        <TableRow
                          key={payment.id}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                            {payment.studentName}
                          </TableCell>
                          <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                          <TableCell>{formatCurrency(payment.amount)}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{payment.method}</TableCell>
                          <TableCell>{payment.description}</TableCell>
                          <TableCell>{getStatusChip(payment.status)}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, payment.id)}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Invoices Tab Content */}
          {activeTab === 'invoices' && (
            <>
              {filteredInvoices.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <ReceiptIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No invoices found
                  </Typography>
                  <Typography color="text.secondary" paragraph>
                    {search ? 'Try changing your search criteria' : 'Get started by creating your first invoice'}
                  </Typography>
                  {!search && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddInvoice}
                      sx={{
                        backgroundColor: THEME_COLORS.primary,
                        '&:hover': { backgroundColor: '#1e1c4e' },
                        mt: 2
                      }}
                    >
                      Create Invoice
                    </Button>
                  )}
                </Box>
              ) : (
                <TableContainer>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Student</TableCell>
                        <TableCell>Issue Date</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredInvoices.map((invoice) => (
                        <TableRow
                          key={invoice.id}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                            INV-{invoice.id.slice(0, 6)}
                          </TableCell>
                          <TableCell>{invoice.studentName}</TableCell>
                          <TableCell>{new Date(invoice.issueDate).toLocaleDateString()}</TableCell>
                          <TableCell>{new Date(invoice.dueDate).toLocaleDateString()}</TableCell>
                          <TableCell>{formatCurrency(invoice.total)}</TableCell>
                          <TableCell>{getStatusChip(invoice.status)}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, invoice.id)}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Reports Tab Content */}
          {activeTab === 'reports' && (
            <Box textAlign="center" py={4}>
              <ChartIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Financial Reports
              </Typography>
              <Typography color="text.secondary" paragraph>
                Generate and view financial reports for your tuition business
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: THEME_COLORS.primary,
                  '&:hover': { backgroundColor: '#1e1c4e' },
                  mt: 2
                }}
              >
                Generate Monthly Report
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* Action Menus */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {activeTab === 'payments' ? (
          <>
            <MenuItem onClick={handleEditPayment}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Payment</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDeletePayment}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: '#F44336' }} />
              </ListItemIcon>
              <ListItemText sx={{ color: '#F44336' }}>Delete Payment</ListItemText>
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={handleEditInvoice}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Invoice</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              handleMenuClose();
              // Implement download logic
            }}>
              <ListItemIcon>
                <PdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download PDF</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              handleMenuClose();
              // Implement email logic
            }}>
              <ListItemIcon>
                <EmailIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Email Invoice</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDeleteInvoice}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: '#F44336' }} />
              </ListItemIcon>
              <ListItemText sx={{ color: '#F44336' }}>Delete Invoice</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
      
      {/* Form Dialogs */}
      <PaymentForm
        open={isPaymentFormOpen}
        onClose={handlePaymentFormClose}
        onSubmit={handlePaymentFormSubmit}
        initialData={currentItem}
      />
      
      <InvoiceForm
        open={isInvoiceFormOpen}
        onClose={handleInvoiceFormClose}
        onSubmit={handleInvoiceFormSubmit}
        initialData={currentItem}
      />
    </Box>
  );
};

export default FinanceManagement;